import React, { useState, useEffect } from 'react'
import { Box, TextField, Button, Typography, Link } from '@mui/material'
import useAccount from '../hooks/useAccount'
import useRouter from '../hooks/useRouter'
import useSnackbar from '../hooks/useSnackbar'
import useLoading from '../hooks/useLoading'
import { postLoginLanding } from '../utils/postLoginLanding'
import TwoStepLoginStep from '../components/auth/TwoStepLoginStep'

export const Home: React.FC = () => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  // Two-step login (#1724): the password was right and a code is due.
  const [mfa, setMfa] = useState<{ challenge: string, enrol: boolean }>()
  const account = useAccount()
  const router = useRouter()
  const snackbar = useSnackbar()
  const loading = useLoading()

  const handleLogin = async () => {
    const result = await account.onLogin(username, password)
    if (result.status === 'mfa_required') {
      setMfa({ challenge: result.challenge, enrol: result.enrol })
    }
  }

  const handleMfaCancel = (reason?: string) => {
    setMfa(undefined)
    setPassword('')
    if (reason) snackbar.error(reason)
  }

  const handleForgotPassword = () => {
    router.navigate('forgot-password')
  }

  // Check for loggedOutMessage URL parameter on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const loggedOutMessage = urlParams.get('loggedOutMessage')

    if (loggedOutMessage) {
      snackbar.error(loggedOutMessage)

      // Clear the parameter from the URL
      const newUrl = window.location.pathname
      window.history.replaceState({}, '', newUrl)
    }
  }, [])

  // Already signed in: go where a fresh login would go (the agent start
  // screen for agent users, Tables otherwise).
  useEffect(() => {
    if (!account.initialized) return
    if (!account.user) return
    const landing = postLoginLanding(!!account.user.domain_permissions?.canAccessAgent, account.customer?.name, account.job?.name)
    if (!landing) return
    router.navigate(landing.name, landing.params)
  }, [account.initialized, account.user, account.customer, account.job])

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      {/* Left section - hidden on small screens (task pane) */}
      <Box sx={{ width: '50%', bgcolor: 'white', display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
        <img
          src="/img/platinum-banner.gif"
          alt="Platinum Title Banner"
          style={{ width: '100%', maxWidth: '100%', height: 'auto' }}
        />
      </Box>

      {/* Right section - full width on small screens */}
      <Box sx={{ width: { xs: '100%', sm: '50%' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          sx={{
            maxWidth: 400,
            width: '100%',
            p: 3,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {mfa ? (
            <TwoStepLoginStep name={username} challenge={mfa.challenge} enrol={mfa.enrol} onCancel={handleMfaCancel} />
          ) : (
          <>
          <Typography variant="h4" component="h1" gutterBottom>
            Login to Platinum
          </Typography>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault()
              void handleLogin()
            }}
          >
            <TextField
              fullWidth
              autoFocus
              autoComplete="username"
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              margin="normal"
              required
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              autoComplete="current-password"
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
            />
            <Button
              fullWidth
              type="submit"
              variant="contained"
              color="info"
              sx={{ mt: 2, minHeight: '40px' }}
              disabled={loading.loading}
            >
              Login
            </Button>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link
                component="button"
                variant="body2"
                onClick={handleForgotPassword}
                sx={{
                  color: 'info.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Recover/Change Password
              </Link>
            </Box>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link
                component="a"
                variant="caption"
                href="https://bayesprice.com/eula"
                target="_blank"
                sx={{
                  color: 'info.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Terms of use
              </Link>
              {' · '}
              <Link
                component="a"
                variant="caption"
                href="https://www.bayesprice.com/software-privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'info.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Privacy
              </Link>
              {' · '}
              <Link
                component="a"
                variant="caption"
                href="/help"
                sx={{
                  color: 'info.main',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Help
              </Link>
            </Box>
          </Box>
          </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default Home
